import { useApolloClient } from '@apollo/client';
import type { Education } from '@seek/ca-myprofile-utils';
import { Qualifications } from '@seek/profile-read-ui';
import { useTranslations } from '@vocab/react';
import { Alert, Text, Stack } from 'braid-design-system';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Card } from 'src/components/Card/Card';
import { CardAction } from 'src/components/Card/CardAction/CardAction';
import { FormDrawer } from 'src/components/FormDrawer/FormDrawer';
import { LoadingCardItem } from 'src/components/LoadingBlock/LoadingBlock';
import { CREATE_RESUME_PAGE, EDUCATION } from 'src/components/Router';
import { useId } from 'src/components/Router/useId';
import { useNavigateRoute } from 'src/components/Router/useNavigateRoute';
import { usePage } from 'src/components/Router/usePage';
import { EDIT, ADD } from 'src/consts';
import { EDUCATION_FORM } from 'src/context/FormContext';
import { certsyLinkClicked } from 'src/modules/analytics/certsy/actionCreators';
import type { CertsyLinkPosition } from 'src/modules/analytics/certsy/eventNames';
import { analyticsClient } from 'src/modules/analytics/index.ts';
import { useAppConfig } from 'src/modules/appConfig';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import {
  retryParent,
  getFailedActionsForParent,
} from 'src/modules/redux-blend';
import * as Schema from 'src/types/graphql';

import { trackUnconfirmedImpression } from '../../../../modules/impression-tracking';
import { setRouteAsTouched } from '../../CreateResume/actionCreators.ts';
import { usePersonalDetails } from '../../PersonalDetails/data.ts';
import { EditEducation } from '../EditEducation/EditEducation';
import {
  useAddUnconfirmedQualification,
  useGetConfirmedQualifications,
  useGetUnconfirmedQualifications,
  useRejectUnconfirmedQualification,
} from '../data';
import { qualificationFormOrEducationUpdateToConfirmQualificationInput } from '../utils';

import translations from './.vocab';
import { getAnalyticsCustomProps } from './utils.ts';

export const ReadEducation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigateRoute();
  const apolloClient = useApolloClient();

  const { t } = useTranslations(translations);

  const { zone, language: languageCode, brand, isUnifiedSite } = useAppConfig();
  const blend = useTypedSelector((state) => state.blend);
  const showRetry = getFailedActionsForParent({ blend }, EDUCATION).length > 0;
  const page = usePage();

  const isCreateResumePage = page === 'create-resume-page';

  const [addUnconfirmedQualificationFn] = useAddUnconfirmedQualification(ADD);
  const [rejectUnconfirmedQualificationFn] =
    useRejectUnconfirmedQualification();

  const [drawerTitle, setDrawerTitle] = useState<string>('');

  const {
    confirmedQualifications,
    loading: confirmedQualificationsLoading,
    error: confirmedQualificationsError,
  } = useGetConfirmedQualifications();

  const {
    unconfirmedQualifications,
    loading: unconfirmedQualificationsLoading,
  } = useGetUnconfirmedQualifications();

  const isEmpty =
    [...confirmedQualifications, ...unconfirmedQualifications].length === 0;

  const paramId = useId();
  const activeQualification = [
    ...confirmedQualifications,
    ...unconfirmedQualifications,
  ].find((qualification) => qualification.id === paramId);

  useEffect(() => {
    if (activeQualification) {
      setDrawerTitle(t('Edit education'));
    } else {
      setDrawerTitle(t('Add education'));
    }
  }, [t, activeQualification]);

  const editQualificationFn = (id: string, confirmed: boolean) => {
    analyticsClient.cardOpened({
      ...(!confirmed ? { origin: 'resume parsing nudge' } : {}),
      currentPageSection: 'education',
      currentPageSectionState: 'edit',
      customProps: getAnalyticsCustomProps({
        qualificationId: activeQualification?.id,
        confirmedQualifications,
      }),
    });

    if (page === CREATE_RESUME_PAGE) {
      setRouteAsTouched(EDUCATION);
    }

    navigate({
      card: EDUCATION,
      mode: EDIT,
      id,
    });
  };

  // Ensure personal details are loaded before rendering to prevent
  // trackUnconfirmedQualificationImpression from firing too early
  const { loading: personalDetailsLoading } = usePersonalDetails();

  const isLoading = Boolean(
    unconfirmedQualificationsLoading ||
    confirmedQualificationsLoading ||
    confirmedQualificationsError,
  );

  const retryErrors = () => dispatch(retryParent(EDUCATION));

  const cardAction = (
    <CardAction
      card={EDUCATION}
      mode={EDIT}
      data={{ automation: 'education-add' }}
      actionText={t('Add education')}
      isEmpty={isEmpty}
      onClick={() => {
        analyticsClient.cardOpened({
          currentPageSection: 'education',
          currentPageSectionState: 'add',
          customProps: getAnalyticsCustomProps({
            qualificationId: paramId,
            confirmedQualifications,
          }),
        });

        if (page === CREATE_RESUME_PAGE) {
          setRouteAsTouched(EDUCATION);
        }
      }}
    />
  );

  const onCertsyLinkClick = useCallback(
    (linkPosition: CertsyLinkPosition, qualification: Education) => () => {
      dispatch(
        certsyLinkClicked({
          linkAction: 'verify',
          linkPosition,
          ...(qualification.verificationUrl
            ? { linkUrl: qualification.verificationUrl }
            : {}),
          credentialType: 'Qualification',
          currentPageSection: 'education',
        }),
      );
    },
    [dispatch],
  );

  return (
    <>
      <Card
        name={EDUCATION}
        title={t('Education')}
        emptyCardText={t('Tell employers about your education')}
        isEmpty={isEmpty}
        retryAction={showRetry ? retryErrors : undefined}
        cardAction={cardAction}
        loading={isLoading}
        loader={
          <Stack space="small">
            <LoadingCardItem />
            <LoadingCardItem />
            <LoadingCardItem />
          </Stack>
        }
      >
        <Stack space="small">
          {unconfirmedQualifications.length > 0 && (
            <>
              <Alert tone="promote">
                <Text>
                  {t('We found these qualifications in your resumé. Add...', {
                    brand,
                  })}
                </Text>
              </Alert>
              <Qualifications
                displayVariant="card"
                qualifications={unconfirmedQualifications.map(
                  (qualification) => ({
                    ...qualification,
                    confirmed: false,
                    addUnconfirmedQualification: (_id, contentRead) =>
                      addUnconfirmedQualificationFn(
                        {
                          qualification:
                            qualificationFormOrEducationUpdateToConfirmQualificationInput(
                              qualification,
                            ),
                          languageCode,
                          zone,
                          isUnifiedSite,
                        },
                        { contentRead },
                      ),
                    rejectUnconfirmedQualification: (id) =>
                      rejectUnconfirmedQualificationFn({
                        qualification: {
                          id,
                          dataType: Schema.UnconfirmedDataType.Education,
                          tracking: qualification.tracking || null,
                        },
                        languageCode,
                        zone,
                        isUnifiedSite,
                      }),
                    editQualification: editQualificationFn,
                    trackUnconfirmedQualificationImpression: (roleData) =>
                      personalDetailsLoading
                        ? null
                        : trackUnconfirmedImpression(
                            { type: 'qualification', data: roleData },
                            apolloClient,
                          ),
                    zone,
                  }),
                )}
              />
            </>
          )}

          {confirmedQualifications.length > 0 ? (
            <Qualifications
              displayVariant="card"
              qualifications={confirmedQualifications.map((qualification) => ({
                ...qualification,
                confirmed: true,
                editQualification: editQualificationFn,
                onVerifyClick: onCertsyLinkClick('main card', qualification),
                onCertsyClick: onCertsyLinkClick('pop up', qualification),
                showCertsyFunctionality: !isCreateResumePage,
                zone,
              }))}
            />
          ) : null}
        </Stack>
      </Card>
      <FormDrawer
        title={drawerTitle}
        component={EditEducation}
        card={EDUCATION}
        formName={EDUCATION_FORM}
      />
    </>
  );
};
